import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'next-i18next';
import { Map as MapIcon } from 'lucide-react';
import type { ListingCardData } from 'components/listing-card';
import type { ListingParams } from 'contexts/preferences/listing-params/types';
import type { InsightsResponse, MapPageCluster, SingleAddress } from '@zoocasa/go-search';
import type { TenantId } from 'tenants/types';
import type { ReactNode, MutableRefObject } from 'react';
import type { Bounds, LatLngPoint } from 'components/search-es/types';
import type { Breadcrumb } from 'components/breadcrumbs';
import { buildClassName } from 'utils/build-class-name';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { searchFilterIds } from 'constants/test-constants';
import {
  GTM_CLICK_HYBRID_PAGE_DESKTOP_EXPAND_MAP,
  GTM_CLICK_HYBRID_PAGE_DESKTOP_HIDE_MAP,
  GTM_CLICK_HYBRID_PAGE_DESKTOP_RESTORE_MAP,
  GTM_CLICK_HYBRID_PAGE_DESKTOP_SHOW_LIST,
  GTM_CLICK_HYBRID_PAGE_MOBILE_MAP_TOGGLE,
} from 'constants/events';
import { localizedFilterHeading, localizedListingsHeading } from './localized-heading';
import useOutsideClickHandler from 'hooks/use-outside-click-handler';
import { useIsTabletLargeUpV2 } from 'hooks/use-breakpoint-v2';
import { isTenantExpCA, isTenantExpUS } from '@tenants';
import { LeftRail } from 'ui/area-map-chrome/rail/left-rail';
import { useRailMenuConfig } from 'ui/area-map-chrome/rail/use-rail-menu-config';
import { MobileNavBar } from 'ui/area-map-chrome/rail/mobile-nav-bar';
import { TopBar } from 'ui/area-map-chrome/chrome/top-bar';
import { MobileTopBar } from 'ui/area-map-chrome/chrome/mobile-top-bar';
import { MapPane } from './panes/map-pane';
import { ListingsColumn, type ListingsStatsBarData } from './panes/listings-column';
import { ListingsViewToggle } from './panes/listings-view-toggle';
import { CollectionsPane } from './panes/collections-pane';
import { InsightsPane } from './panes/insights-pane';
import { SmartSearchPanel } from './smart-search/smart-search-panel';
import MyLinkMyLeadCreate from 'components/my-link-my-lead-create';
import styles from './style.module.scss';

import type { AreaPageV2InsightsContent } from './panes/insights-pane';
import type { AreaPageV2Contracts } from './contracts';
import type { MapComponentMethods } from 'components/search-es/map/Map';
import { trackAreaPageV2Event } from './track-area-page-v2-event';
import { hasHeadingRelevantFilter } from './has-heading-relevant-filter';

export type AreaPageV2ViewProps = {
  contracts: AreaPageV2Contracts;
  insights: AreaPageV2InsightsContent;
  electedAddress: SingleAddress;
  heading: string;
  listings: readonly ListingCardData[];
  pagination: {
    page: number;
    size: number;
    count: number;
    total: number;
  };
  listingParams: ListingParams;
  footerSlot: ReactNode;
  tenantId: TenantId;
  showsMyLinkMyLeadCreate?: boolean;
  isCrawler?: boolean;
  hideMap?: boolean;
  /** Area's own info-page slug (e.g. "toronto-on") — backfills avg price/sqft in the stats bar. */
  citySlug?: string;
  /** Market insights for the current area, driving the AEO stats bar and FAQ section. */
  insightsData?: InsightsResponse;
  /** The stats bar's market and its stats. */
  statsBar?: ListingsStatsBarData;
  /** Area hierarchy crumbs shown under the listings pagination. */
  breadcrumbs?: readonly Breadcrumb[];
  /** Renders the feeds' compliance disclaimers under the listings column (US / military base areas). */
  showProviderDisclaimer?: boolean;
  clusters: MapPageCluster[];
  highlightedListingId: number | null;
  isMapLoading: boolean;
  areaPolygonWkt?: string;
  mapCenter: LatLngPoint;
  mapZoom: number;
  /** Imperative handle to the map, forwarded to MapPane so a selected saved search can pan it. */
  mapRef?: MutableRefObject<MapComponentMethods | null>;
  /** Shows the "Remove Boundary" pill over the map when supplied; omitted for province/country-level areas. */
  onRemoveBoundary?: () => void;
  isMapCenterPointLoading?: boolean;
  isMapCenterPointError?: boolean;
  onMapBoundingRegionChanged: (oldBounds: Bounds | null, newBounds: Bounds) => void;
  onMapZoomChanged: (oldZoom: number, newZoom: number) => void;
  onListingHover: (listingId: number | null) => void;
  setPageNumber: (pageNumber: number) => void;
};

export function AreaPageV2View({
  contracts,
  insights,
  electedAddress,
  heading,
  listings,
  pagination,
  listingParams,
  footerSlot,
  tenantId,
  showsMyLinkMyLeadCreate = false,
  isCrawler = false,
  hideMap = false,
  citySlug,
  insightsData,
  statsBar,
  breadcrumbs,
  showProviderDisclaimer = false,
  clusters,
  highlightedListingId,
  isMapLoading,
  areaPolygonWkt,
  mapCenter,
  mapZoom,
  mapRef,
  onRemoveBoundary,
  isMapCenterPointLoading = false,
  isMapCenterPointError = false,
  onMapBoundingRegionChanged,
  onMapZoomChanged,
  onListingHover,
  setPageNumber,
}: AreaPageV2ViewProps) {
  const menuConfig = useRailMenuConfig(tenantId);
  const isExpChrome = isTenantExpCA(tenantId) || isTenantExpUS(tenantId);
  // MapV2 binds its pan and zoom handlers to MapKit once, when MapKit finishes
  // loading, and never rebinds. Handing it a different function when the map is
  // parked would therefore stick: if loading finished while another destination
  // was showing, that function stays bound and the map never reports again, so
  // even panning and zooming on the map itself would stop fetching. These
  // wrappers keep one identity for the page's life and read the destination
  // when the event arrives, so a parked map stays quiet without being muted
  // permanently.
  const isMapRef = useRef(contracts.destination === 'map');
  useEffect(() => {
    isMapRef.current = contracts.destination === 'map';
  }, [contracts.destination]);

  const handleMapBoundingRegionChanged = useCallback((oldBounds: Bounds | null, newBounds: Bounds) => {
    if (!isMapRef.current) return;
    onMapBoundingRegionChanged(oldBounds, newBounds);
  }, [onMapBoundingRegionChanged]);

  const handleMapZoomChanged = useCallback((oldZoom: number, newZoom: number) => {
    if (!isMapRef.current) return;
    onMapZoomChanged(oldZoom, newZoom);
  }, [onMapZoomChanged]);

  const isCollections = contracts.destination === 'collections';
  const isInsights = contracts.destination === 'insights';
  const isMap = contracts.destination === 'map';
  const { t } = useTranslation(Namespaces.COMMON);
  const mapLabel = t('area_page_v2.rail.map');
  const collectionsLabel = t('area_page_v2.rail.collections');
  const insightsLabel = t('area_page_v2.rail.insights');
  const navLabel = t('area_page_v2.rail.nav_label');
  const savedSearchesLabel = t('area_page_v2.rail.saved_searches');
  const menuLabel = t('area_page_v2.rail.menu');
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const [isListingsFullScreen, setIsListingsFullScreen] = useState(false);
  const [isMobileListView, setIsMobileListView] = useState(false);
  // Hides the mobile map/listings-count toggle while the cluster/building drawer (Map's
  // own bottom sheet) is open on mobile — that sheet already has its own close button.
  const [isMapDrawerOpen, setIsMapDrawerOpen] = useState(false);
  // Hides the whole mobile nav bar while the filters drawer is open, same as it's
  // already covered while the Saved Searches sheet is open.
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const smartSearchDockRef = useOutsideClickHandler<HTMLDivElement>(contracts.onCloseSmartSearch);
  const mobileSmartSearchDockRef = useOutsideClickHandler<HTMLDivElement>(contracts.onCloseSmartSearch);
  // Desktop and mobile render their own top bars (CSS-toggled), but both would
  // mount a SmartSearchPanel when open — two instances double every parse and
  // suggestion fetch. Mount only the one for the active viewport.
  const isTabletLargeUp = useIsTabletLargeUpV2();

  // AreaPageV2 renders its own chrome with no site top-nav, but the body-container
  // still reserves eXp nav padding for CA/US, leaving a gap at the top. Flag the
  // body so the padding can be dropped for this page only (see application.scss).
  useEffect(() => {
    if (!isExpChrome) {
      return;
    }
    const bodyClass = 'area-page-v2-exp-nav';
    document.body.classList.add(bodyClass);
    return () => document.body.classList.remove(bodyClass);
  }, [isExpChrome]);

  // Move accessibility widget to the right side on area page v2
  useEffect(() => {
    const bodyClass = 'area-page-v2-accessibility-right';
    document.body.classList.add(bodyClass);
    return () => document.body.classList.remove(bodyClass);
  }, []);

  const handleSetListingsFullScreen = (fullScreen: boolean) => {
    if (fullScreen) {
      trackAreaPageV2Event(GTM_CLICK_HYBRID_PAGE_DESKTOP_HIDE_MAP);
    } else if (isListingsFullScreen) {
      trackAreaPageV2Event(GTM_CLICK_HYBRID_PAGE_DESKTOP_RESTORE_MAP);
    }
    setIsListingsFullScreen(fullScreen);
    if (fullScreen) {
      setIsMapFullscreen(false);
    }
  };

  const handleSetMapFullscreen = (mapFullscreen: boolean) => {
    if (mapFullscreen) {
      trackAreaPageV2Event(GTM_CLICK_HYBRID_PAGE_DESKTOP_EXPAND_MAP);
    } else if (isMapFullscreen) {
      trackAreaPageV2Event(GTM_CLICK_HYBRID_PAGE_DESKTOP_SHOW_LIST);
    }
    setIsMapFullscreen(mapFullscreen);
    if (mapFullscreen) {
      setIsListingsFullScreen(false);
    }
  };

  const handleFiltersOpenChange = (isOpen: boolean) => {
    setIsFiltersOpen(isOpen);
    contracts.onFiltersPanelOpenChange(isOpen);
  };

  // Classic filter-aware heading (e.g. "Real Estate & Homes for Sale in Toronto"),
  // shown on mobile above the map-area listing count.
  const headingParams = {
    areaName: contracts.areaName ?? '',
    areaType: electedAddress.addressType,
    filter: listingParams.filter,
    city: electedAddress.subDivision || undefined,
    neighbourhood: electedAddress.neighbourhood || undefined,
  };
  const filterAwareHeading = localizedFilterHeading(headingParams, t);

  // `heading` is rendered server-side once per navigation, but this page applies filters by
  // rewriting the URL in place (no SSR), so it would keep saying "for Sale" after a switch to
  // filter with the same helper the server uses; until then keep the server's answer-style
  // heading, which reads better for an unfiltered area.
  const listingsHeading = localizedListingsHeading(contracts.areaName, heading, t);
  const displayHeading = hasHeadingRelevantFilter(listingParams.filter) ? filterAwareHeading : listingsHeading;

  return (
    <div data-testid="AreaPageV2" className={buildClassName(styles.root, isExpChrome && styles.expChrome)}>
      <div className={styles.rail}>
        <LeftRail
          destination={contracts.destination}
          onOpenMap={contracts.onOpenMap}
          onOpenCollections={contracts.onOpenCollections}
          onOpenInsights={contracts.onOpenInsights}
          isExpLogo={isExpChrome}
          mapLabel={mapLabel}
          collectionsLabel={collectionsLabel}
          insightsLabel={insightsLabel}
          menuLabel={menuLabel}
          tenantId={tenantId}
          logoSrc={menuConfig.logoSrc}
          logoAlt={menuConfig.logoAlt}
          navConfig={menuConfig.navConfig}
        />
      </div>
      <MobileNavBar
        destination={contracts.destination}
        onOpenMap={contracts.onOpenMap}
        onOpenCollections={contracts.onOpenCollections}
        onOpenInsights={contracts.onOpenInsights}
        onOpenSavedSearches={contracts.onOpenSavedSearches}
        filter={contracts.filter}
        onViewAllSavedSearches={contracts.onViewAllSavedSearches}
        navConfig={menuConfig.navConfig}
        logoSrc={menuConfig.logoSrc}
        logoAlt={menuConfig.logoAlt}
        localeMenu={menuConfig.localeMenu}
        isExpLogo={isExpChrome}
        isHidden={isFiltersOpen}
        mapLabel={mapLabel}
        collectionsLabel={collectionsLabel}
        insightsLabel={insightsLabel}
        navLabel={navLabel}
        savedSearchesLabel={savedSearchesLabel}
        menuLabel={menuLabel}
      />
      <div className={styles.main}>
        {isMap && (
          <>
            <div className={styles.desktopTopBar}>
              <TopBar
                areaName={contracts.areaName}
                onLocationSearchClick={contracts.onLocationSearchClick}
                filter={contracts.filter}
                onViewAllSavedSearches={contracts.onViewAllSavedSearches}
                onFiltersValueChange={contracts.onFiltersApplied}
                onApplySavedSearch={contracts.onApplySavedSearch}
                filtersResultsCount={contracts.filtersResultsCount}
                onSavedSearchesClick={contracts.onOpenSavedSearches}
                onFiltersOpenChange={handleFiltersOpenChange}
                isSmartSearchEnabled={contracts.isSmartSearchEnabled}
                isSmartSearchOpen={contracts.isSmartSearchOpen}
                smartSearchQuery={contracts.smartSearchQuery}
                onSmartSearchOpen={contracts.onOpenSmartSearch}
                trailingAction={showsMyLinkMyLeadCreate ? <MyLinkMyLeadCreate /> : undefined}
                smartSearchPanel={isTabletLargeUp && contracts.isSmartSearchOpen && (
                  <div ref={smartSearchDockRef}>
                    <SmartSearchPanel
                      areaName={contracts.areaName}
                      areaSlug={contracts.filter.slug}
                      resultsCount={contracts.filtersResultsCount}
                      onCountListings={contracts.onCountListingsForFilter}
                      onFiltersApplied={contracts.onFiltersApplied}
                      onSortApplied={contracts.onSortChanged}
                      onLocationSelected={contracts.onLocationSearchClick}
                      onDismiss={contracts.onCloseSmartSearch}
                      initialQuery={contracts.smartSearchQuery}
                      initialTags={contracts.filter.smartTagNames}
                      initialFilter={contracts.filter}
                      onQueryChanged={contracts.onSmartSearchQueryChange}
                      autoFocus
                    />
                  </div>
                )}
              />
            </div>
            {!isMobileListView && (
              <header data-testid="AreaPageV2-MobileTopBar" className={styles.mobileTopBar}>
                <MobileTopBar
                  areaName={contracts.areaName}
                  onLocationSearchClick={contracts.onLocationSearchClick}
                  filter={contracts.filter}
                  onFiltersValueChange={contracts.onFiltersApplied}
                  filtersResultsCount={contracts.filtersResultsCount}
                  isSmartSearchEnabled={contracts.isSmartSearchEnabled}
                  isSmartSearchOpen={contracts.isSmartSearchOpen}
                  smartSearchQuery={contracts.smartSearchQuery}
                  onSmartSearchOpen={contracts.onOpenSmartSearch}
                  trailingAction={showsMyLinkMyLeadCreate ? <MyLinkMyLeadCreate /> : undefined}
                  smartSearchPanel={!isTabletLargeUp && contracts.isSmartSearchOpen && (
                    <div ref={mobileSmartSearchDockRef}>
                      <SmartSearchPanel
                        areaName={contracts.areaName}
                        areaSlug={contracts.filter.slug}
                        resultsCount={contracts.filtersResultsCount}
                        onCountListings={contracts.onCountListingsForFilter}
                        onFiltersApplied={contracts.onFiltersApplied}
                        onSortApplied={contracts.onSortChanged}
                        onLocationSelected={contracts.onLocationSearchClick}
                        onDismiss={contracts.onCloseSmartSearch}
                        initialQuery={contracts.smartSearchQuery}
                        initialTags={contracts.filter.smartTagNames}
                        initialFilter={contracts.filter}
                        onQueryChanged={contracts.onSmartSearchQueryChange}
                        autoFocus
                      />
                    </div>
                  )}
                  onFiltersOpenChange={handleFiltersOpenChange}
                />
              </header>
            )}
          </>
        )}
        {isCollections && (
          <div className={buildClassName(styles.collectionsWorkspace, styles.destinationPane)}>
            <CollectionsPane
              onApplySavedSearch={contracts.onApplySavedSearch}
              initialTab={contracts.collectionsInitialTab}
              onTabChange={contracts.onCollectionsTabChange}
            />
          </div>
        )}
        {(isInsights || isCrawler) && (
          <div className={styles.destinationPane}>
            <InsightsPane content={insights} />
          </div>
        )}
        {/*
          Keep the map mounted across Map, Insights and Collections view, so map is stable and doesn't have to reload between these views.
        */}
        <div
          className={buildClassName(
            styles.workspace,
            isMapFullscreen && styles.mapFullscreen,
            isListingsFullScreen && styles.listingsFullScreen,
            isMobileListView && styles.mobileListView,
            hideMap && styles.provinceFullWidth,
            !isMap && styles.workspaceParked
          )}
          aria-hidden={!isMap}
          data-testid="AreaPageV2-MapWorkspace"
        >
          {!hideMap && (
            <div className={styles.mapCell}>
              <MapPane
                electedAddress={electedAddress}
                mapRef={mapRef}
                clusters={clusters}
                listings={listings}
                highlightedListingId={highlightedListingId}
                isMapLoading={isMapLoading}
                areaPolygonWkt={areaPolygonWkt}
                center={mapCenter}
                zoom={mapZoom}
                isMapCenterPointLoading={isMapCenterPointLoading}
                isMapCenterPointError={isMapCenterPointError}
                onMapBoundingRegionChanged={handleMapBoundingRegionChanged}
                onMapZoomChanged={handleMapZoomChanged}
                onDrawerVisibilityChange={setIsMapDrawerOpen}
              />
              {isMap && isMapFullscreen && (
                <ListingsViewToggle
                  className={styles.mapFullscreenViewToggle}
                  isFullScreen={isListingsFullScreen}
                  onSetFullScreen={handleSetListingsFullScreen}
                  isMapFullscreen={isMapFullscreen}
                  onSetMapFullscreen={handleSetMapFullscreen}
                />
              )}
              {isMap && !isMapDrawerOpen && !hideMap && (
                <div className={styles.mobileActionBar}>
                  {onRemoveBoundary && !isMobileListView && (
                    <button
                      type="button"
                      className={styles.removeBoundary}
                      onClick={onRemoveBoundary}
                      data-testid={searchFilterIds.removeBoundaryButton}
                    >
                      {t(LOCALE_COMMON_PATHS.HYBRID_PAGE_REMOVE_BOUNDARY, { defaultValue: 'Remove Boundary' })}
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.mobileViewToggle}
                    data-testid="AreaPageV2-MobileViewToggle"
                    onClick={() => {
                      trackAreaPageV2Event(GTM_CLICK_HYBRID_PAGE_MOBILE_MAP_TOGGLE);
                      setIsMobileListView(prev => !prev);
                    }}
                  >
                    {isMobileListView ? (
                      <>
                        <MapIcon className={styles.mobileViewToggleIcon} size={18} strokeWidth={2} aria-hidden />
                        {t('area_page_v2.listings.map', { defaultValue: 'Map' })}
                      </>
                    ) : (
                      t('area_page_v2.listings.listings_count', {
                        number: pagination.count,
                        defaultValue: 'View {{number}} Listings',
                      })
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
          <div
            className={buildClassName(styles.listingsPane, isMapFullscreen && styles.listingsPaneHidden)}
            data-testid="AreaPageV2-ListingsPane"
            hidden={isMapFullscreen || !isMap}
          >
            <ListingsColumn
              heading={displayHeading}
              mobileHeading={filterAwareHeading}
              listings={listings}
              pagination={pagination}
              listingParams={listingParams}
              footerSlot={footerSlot}
              headerAction={
                showsMyLinkMyLeadCreate && isMobileListView
                  ? <MyLinkMyLeadCreate />
                  : undefined
              }
              scrollRef={contracts.listingsScrollRef}
              onListingHover={onListingHover}
              onSortChanged={contracts.onSortChanged}
              isFullScreen={isListingsFullScreen}
              onSetFullScreen={handleSetListingsFullScreen}
              isMobileListView={isMobileListView}
              isMapFullscreen={isMapFullscreen}
              onSetMapFullscreen={handleSetMapFullscreen}
              hideViewToggle={hideMap}
              setPageNumber={setPageNumber}
              isLoading={false}
              areaName={contracts.areaName}
              citySlug={citySlug}
              insightsData={insightsData}
              statsBar={statsBar}
              breadcrumbs={breadcrumbs}
              showProviderDisclaimer={showProviderDisclaimer}
              onMoreStatsClick={contracts.onOpenInsights}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
